import React, { useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import styled from 'styled-components';
import startVideoClockSync from './videoClockSync';

const VideoContainer = styled.div`
  width: 100%;
  height: 100%;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const ErrorMessage = styled.div`
  color: #ff6b6b;
  font-size: 1.5rem;
  text-align: center;
  padding: 2rem;
`;

/**
 * VideoPlayer - Display videos with playback controls
 *
 * @param {string} url - Video URL (mp4, webm)
 * @param {boolean} autoplay - Start playing automatically (default: true)
 * @param {boolean} loop - Loop playback (default: false)
 * @param {boolean} muted - Mute audio (default: false)
 * @param {boolean} showControls - Show playback controls (default: false)
 */
const VideoPlayer = forwardRef(({
  url,
  autoplay = true,
  loop = false,
  muted = false,
  showControls = false,
  syncEpoch = null,   // server-clock ms of the synchronized reveal
  clockOffset = 0,    // serverTime − clientTime, from clock-ping
}, ref) => {
  const videoRef = useRef(null);

  // Expose control methods via ref
  useImperativeHandle(ref, () => ({
    play: () => videoRef.current?.play(),
    pause: () => videoRef.current?.pause(),
    restart: () => {
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
        videoRef.current.play();
      }
    },
    seek: (time) => {
      if (videoRef.current) {
        videoRef.current.currentTime = time;
      }
    },
    getElement: () => videoRef.current
  }));

  useEffect(() => {
    if (videoRef.current && autoplay) {
      // Autoplay with muted fallback for browser policies
      videoRef.current.play().catch(() => {
        // If autoplay fails, try muted
        videoRef.current.muted = true;
        videoRef.current.play().catch(() => {
          console.warn('Video autoplay failed even when muted');
        });
      });
    }
  }, [url, autoplay]);

  // Multi-screen sync: hold playback position to the shared epoch
  useEffect(() => {
    if (!syncEpoch || !videoRef.current) return undefined;
    return startVideoClockSync(videoRef.current, { syncEpoch, clockOffset });
  }, [url, syncEpoch, clockOffset]);

  if (!url) {
    return (
      <VideoContainer>
        <ErrorMessage>
          Video URL puuttuu
        </ErrorMessage>
      </VideoContainer>
    );
  }

  return (
    <VideoContainer>
      <StyledVideo
        ref={videoRef}
        src={url}
        loop={loop}
        muted={muted}
        controls={showControls}
        playsInline
      />
    </VideoContainer>
  );
});

VideoPlayer.displayName = 'VideoPlayer';

export default VideoPlayer;
